import { Property } from "csstype";
import * as React from "react";
import { Hint } from "react-ui";

import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { themed } from "../../Internals/ThemeProvider/ThemedWorkaround";
import { TourTargetElement } from "../../Internals/TourManagement/TourTargetElement";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { emptyRef } from "../../TypingUtils";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";
import { ensureArrayIsNotEmpty, isNullOrWhiteSpace } from "../../../../../Common/TypingUtils";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

import * as styles from "./Text.styles";
import { TextTestPageObject } from "./TextTestPageObject";

export interface TextProps extends ControlValidationProps, ControlTooltipProps {
    hint?: string | React.ReactNode;
    bindingPath: AbsoluteModelPath;
    isTooltipEnabled?: boolean;
    quotes?: string;
    showError?: boolean;
    type?: "optional";
    onClick?: () => void;
    isLink?: boolean;
    value: undefined | null | string;
    marginTop?: string | number;
    marginBottom?: string | number;
    wrapTagName?: "span" | "div";
    color?: string;
    fontSize?: string | number;
    fontWeight?: Property.FontWeight;
    lineHeight?: string | number;
    textAlign?: Property.TextAlign;
    width?: number | string;
    rouble?: boolean;
    currency?: boolean;
    currencyIntegerDigits?: number;
    currencyFractionDigits?: number;
    isReadOnly?: boolean;
    hideValidation?: boolean;
    "data-tid"?: string;
}

export function Text(props: TextProps): JSX.Element {
    const rootRef = React.useRef<HTMLSpanElement>(emptyRef);

    useTestPageObject(() => TextTestPageObject(props.value), [props.value]);

    const handleFocusRequest = (): void => {
        if (rootRef.current != undefined) {
            tryScrollIntoViewWithFallback(rootRef.current, { block: "center", inline: "center" });
        }
    };

    function renderValue(): JSX.Element {
        const { value, quotes, type } = props;

        const elementStyles: React.CSSProperties = {
            fontSize: props.fontSize,
            fontWeight: props.fontWeight,
            lineHeight: props.lineHeight,
            textAlign: props.textAlign,
            width: props.width,
        };

        const isValue = Boolean(type !== "optional" || value);
        const text = (
            <TourTargetElement controlId={props.bindingPath.toLegacyPath()}>
                <span
                    ref={rootRef}
                    style={elementStyles}
                    onClick={props.onClick}
                    // @ts-ignore
                    css={themed(
                        isValue && styles.breakWords,
                        isValue && styles.textContainer,
                        props.isLink && styles.linkedText,
                        !isValue && styles.emptyOptionalText
                    )}
                    data-tid={props["data-tid"]}>
                    {!isValue && "–"}
                    {isValue && (quotes?.[0] ?? "")}
                    {isValue && props.currency
                        ? formatCurrencyValue(value, props.currencyFractionDigits, props.rouble)
                        : value}
                    {isValue && (quotes?.[1] ?? "")}
                </span>
            </TourTargetElement>
        );

        if (props.hint) {
            return <Hint text={props.hint}>{text}</Hint>;
        }

        return text;
    }

    function renderErrorOrWarning(): JSX.Element {
        const { value, error, showError = true, warning } = props;

        const elementStyles: React.CSSProperties = {
            fontSize: props.fontSize,
            fontWeight: props.fontWeight,
            lineHeight: props.lineHeight,
            textAlign: props.textAlign,
            width: props.width,
        };

        const message = error ?? warning;

        if (!showError) {
            return <span ref={rootRef} style={elementStyles} />;
        }
        if (!value) {
            return (
                <span
                    ref={rootRef}
                    // @ts-ignore
                    css={themed(
                        styles.breakWords,
                        styles.textContainer,
                        Boolean(error) && styles.textError,
                        !error && Boolean(warning) && styles.textWarning
                    )}
                    style={elementStyles}>
                    {message}
                </span>
            );
        }

        return (
            <Tooltip
                trigger={props.isTooltipEnabled ? "hover" : "closed"}
                value={message}
                pos={props.tooltipPosition}
                width={props.tooltipWidth}>
                <TourTargetElement controlId={props.bindingPath.toLegacyPath()}>
                    <span
                        ref={rootRef}
                        // @ts-ignore
                        css={themed(
                            styles.breakWords,
                            styles.textContainer,
                            Boolean(error) && styles.textError,
                            !error && Boolean(warning) && styles.textWarning
                        )}
                        style={elementStyles}>
                        {value}
                    </span>
                </TourTargetElement>
            </Tooltip>
        );
    }

    const result = (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            {(props.error || props.warning) && !props.isReadOnly && !props.hideValidation
                ? renderErrorOrWarning()
                : renderValue()}
        </FocusManagementObserver>
    );

    if (props.wrapTagName != undefined) {
        const WrapTag = props.wrapTagName;
        return (
            <WrapTag
                style={{
                    color: props.color,
                    marginBottom: props.marginBottom,
                    marginTop: props.marginTop,
                }}>
                {result}
            </WrapTag>
        );
    }
    return result;
}

const thinSpace = "\u2009";
const roubleSign = "\u20bd";

function formatCurrencyValue(
    value: undefined | null | string,
    fractionDigits?: number,
    rouble?: boolean
): undefined | null | string {
    if (value == undefined || isNullOrWhiteSpace(value)) {
        return value;
    }
    const parsedValue = Number(value);
    if (!isFinite(parsedValue)) {
        return value;
    }
    const parts = parsedValue.toString().split(".");
    ensureArrayIsNotEmpty(parts);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thinSpace);
    if (fractionDigits != undefined && fractionDigits > 0) {
        parts[1] = (parts[1] ?? "").padEnd(fractionDigits, "0");
    }
    return parts.join(",") + (rouble ? thinSpace + roubleSign : "");
}
